import React, { useEffect, useState } from 'react';
import { Container, Grid, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { saveAs } from 'file-saver';
import { makeStyles } from '@material-ui/core/styles';
import { Link, useHistory, useLocation } from 'react-router-dom';
import TextTypo from 'components/helpers/TextTypo';
import { Buffer } from 'buffer';
import {
  specificLoanApplicationAction,
  downloadAction,
  zipDownloadAction,
  changeApplicationStatusAction,
  downloadDocumentAction,
} from 'redux/actions/applicationAction';
import NumberFormat from 'react-number-format';
import { Form, Tag, Modal, Breadcrumb, Card, Switch } from 'antd';
import { decodeToken, downloadFile, getStatusColor } from 'utils';
import { CheckCircle, RemoveRedEye } from '@material-ui/icons';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  EyeFilled,
} from '@ant-design/icons';
import Button from 'components/Button';
import CommentForm from 'components/Comment';
import Input from 'components/Input';
import Select from 'components/Select';
import { inputRules } from 'utils';
import moment from 'moment';

const useStyles = makeStyles({
  tableCell: {
    fontWeight: 'bold',
  },
  back: {
    cursor: 'pointer',
    color: '#198754',
  },
});

const SpecificApplication = ({ match }) => {
  const { applicationId } = match.params;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const [approvalForm] = Form.useForm();

  const housePurchaseAmount = Form.useWatch(
    'housePurchaseAmount',
    approvalForm
  );
  const approvedLoanAmount = Form.useWatch('approvedLoanAmount', approvalForm);
  const disbursedDate = Form.useWatch('disbursedDate', approvalForm);
  const maturityDate = Form.useWatch('maturityDate', approvalForm);

  const [isDownloadCompleted, setIsDownloadCompleted] = useState(false);
  const [isZipDownloadCompleted, setIsZipDownloadCompleted] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [statusComment, setStatusComment] = useState();
  const [showModal, setShowModal] = useState(false);
  const [appAction, setAppAction] = useState('');
  const [bankStatus, setBankStatus] = useState('');
  const [showEditor, setShowEditor] = useState(true);
  const [updateOptions, setUpdateOptions] = useState([]);
  const [perfectionStatus, setPerfectionStatus] = useState(true);

  const { application, applicationStatus } = useSelector(
    ({ backendUsers, specificLoanApplication }) => ({
      application: specificLoanApplication,
      applicationStatus: backendUsers?.applicationStatus,
      downloadedDoc: backendUsers?.downloadedDoc,
    })
  );

  const results = useSelector((state) => state.certificate);
  const zip = useSelector((state) => state.zipCertificate);
  const bankId = Number(decodeToken().adminProfile?.scopeValue);

  if (results.data) {
    const buffer = Buffer.from(results.data.fileBlob.data);
    const blob = new Blob([buffer], {
      type: `${results?.data?.fileType};charset=utf-8`,
    });
    if (isDownloadCompleted) {
      saveAs(blob, results.data.fileName);
    }
  }

  if (zip.data) {
    const buffer = Buffer.from(zip.data.fileBlob.data);
    const blob = new Blob([buffer], {
      type: `${zip?.data?.fileType};charset=utf-8`,
    });
    if (isZipDownloadCompleted) {
      saveAs(blob, zip.data.fileName);
    }
  }

  const handleWorkCertificate = () => {
    dispatch(downloadAction(applicationId, 'applicant', 'workCertificate'));
    setIsDownloadCompleted(true);
    setIsZipDownloadCompleted(false);
  };

  const handleSpouseWorkCertificate = () => {
    dispatch(
      downloadAction(applicationId, 'applicant', 'spouse.workCertificate')
    );
    setIsDownloadCompleted(true);
    setIsZipDownloadCompleted(false);
  };

  const handleMaritalCertificate = () => {
    dispatch(
      downloadAction(applicationId, 'applicant', 'maritalStatusCertificate')
    );
    setIsDownloadCompleted(true);
    setIsZipDownloadCompleted(false);
  };

  const handleZipDownload = () => {
    dispatch(zipDownloadAction(applicationId));
    setIsZipDownloadCompleted(true);
    setIsDownloadCompleted(false);
  };

  const handleBack = () => {
    history.goBack();
  };

  useEffect(() => {
    dispatch(specificLoanApplicationAction(applicationId));
  }, []);

  useEffect(() => {
    const isOwnBankProcessing = application?.data?.processingBanks?.includes(bankId) || false;

    if (
      application?.data?.bankStatus === 'Pending' ||
      (application?.data?.bankStatus === 'Under-Process' &&
        !isOwnBankProcessing)
    ) {
      setUpdateOptions([
        {
          value: 'Under-Process',
        },
      ]);
    } else {
      setUpdateOptions([
        {
          value: 'Disbursed',
        },
      ]);
    }
  }, [application]);

  const role = decodeToken().userRole?.RoleName;
  const backendUser = role !== 'Super Admin' && role !== 'Admin';
  const bankUser = role === 'Bank Officer';
  const adminUser = role === 'Admin';
  useEffect(() => {
    if (applicationStatus?.status === 200) {
      dispatch(specificLoanApplicationAction(applicationId));
      setShowModal(false);
    }
    setShowEditor(backendUser);
  }, [applicationStatus]);

  const getUPIlocation = (objString) => {
    if (objString) {
      const obj = JSON.parse(objString);
      const {
        cell: { cellName },
        sector: { sectorName },
        district: { districtName },
        province: { provinceName },
      } = obj || {};
      return `${provinceName} , ${districtName}, ${sectorName}, ${cellName}`;
    }
  };

  let level =
    decodeToken()?.userRole?.RoleName === 'Bank Officer' ? 'bank' : 'backend';

  const handleApproval = () => {
    approvalForm
      .validateFields()
      .then(({ status, otherStatusComment, ...payload }) => {
        if (payload.approvedLoanAmount > payload.housePurchaseAmount) {
          return alert('Loan amount cannot be greater than house cost');
        }
        let applicationStatus;

        if (appAction.action === 'reconfirm') {
          applicationStatus = appAction.action;
          level = 'application';
        } else {
          applicationStatus = level === 'bank' ? status : appAction.action;
          if (status === 'Other') {
            payload.statusComment = otherStatusComment;
          }
        }
        if (level === 'bank' && status === 'Disbursed') {
          payload.perfectionStatus = perfectionStatus
            ? 'Perfected'
            : 'Not Perfected';
        }

        changeApplicationStatusAction(applicationId, applicationStatus, level, {
          ...payload,
        })(dispatch);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleDownload = (docId) => {
    dispatch(downloadDocumentAction(applicationId, docId));
  };

  const onChange = (checked) => {
    setPerfectionStatus(checked);
  };

  const calculateLoanToValueRatio = () => {
    if (!housePurchaseAmount || !approvedLoanAmount) return 0;

    return (approvedLoanAmount / housePurchaseAmount) * 100;
  };

  const calculateTenorInMonths = () => {
    if (!disbursedDate || !maturityDate) return 0;
    const startDate = new Date(disbursedDate);
    const endDate = new Date(maturityDate);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return (diffDays / 30).toFixed(2);
  };

  const availAction = (level) => {
    const role = decodeToken().userRole?.RoleName;

    if (
      role === 'Back-End Officer' &&
      level === 'backend-officer' &&
      !decodeToken().adminProfile?.organization?.toLowerCase()?.includes('brd')
    ) {
      return true;
    }
    if (role === 'Bank Officer' && level === 'bank') {
      return true;
    }
    return false;
  };

  return (
    <div style={{ marginTop: 20 }}>
      <Modal
        title={`${appAction.title || 'Update'} Application`}
        open={showModal}
        onOk={handleApproval}
        okText='Save Changes'
        onCancel={() => setShowModal(false)}
        confirmLoading={applicationStatus?.loading}
        width={700}
      >
        <Form form={approvalForm}>
          {level !== 'bank' ? (
            <>
              {level === 'bank' && appAction.action === 'Approved' && (
                <div>
                  <Form.Item name='approvedLoanAmount' rules={inputRules}>
                    <Input label='Loan Amount Disbursed' required />
                  </Form.Item>
                  <Form.Item name='interestRate'>
                    <Input label='Interest rate' />
                  </Form.Item>
                </div>
              )}
              {level === 'backend' && appAction.action === 'Approved' && (
                <div>
                  <p>
                    {t('application-approval-question')}{' '}
                    <b>{(application?.data?.interestRate * 100).toFixed(2)}%</b>{' '}
                    {t('application-approval-question-end')}
                  </p>
                  <Form.Item name='interestRate'>
                    <Select
                      placeholder=''
                      options={['11.00%', '13.00%'].map((rate) => ({
                        value: rate.replace('%', ''),
                      }))}
                      defaultValue={`${(
                        application?.data?.interestRate * 100
                      ).toFixed(2)}`}
                    />
                  </Form.Item>
                </div>
              )}
              {appAction.action === 'reconfirm' && (
                <div>
                  <p>
                    {t('application-approval-question')}{' '}
                    <b>{(application?.data?.interestRate * 100).toFixed(2)}%</b>
                  </p>
                </div>
              )}
              {appAction.action === 'Rejected' && (
                <div>
                  <Form.Item name='statusComment' rules={inputRules}>
                    <Select
                      placeholder='Select reason'
                      options={[
                        { value: 'Plot Issue' },
                        { value: 'Income Issue' },
                        { value: 'Certificate Issue' },
                        { value: 'Other' },
                      ]}
                      size='large'
                      onChange={setStatusComment}
                    />
                  </Form.Item>
                  {statusComment === 'Other' && (
                    <Form.Item
                      name='otherStatusComment'
                      rules={[
                        {
                          required: true,
                          message: 'Comment is required',
                        },
                      ]}
                    >
                      <Input placeholder='Please specify the reason' />
                    </Form.Item>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <Form.Item name='status' rules={inputRules}>
                <Select
                  options={updateOptions}
                  placeholder='Select status'
                  label='Status'
                  onChange={setBankStatus}
                  required
                />
              </Form.Item>
              {bankStatus === 'Disbursed' && (
                <div
                  style={{
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '10px',
                  }}
                >
                  <Form.Item name='housePurchaseAmount' rules={inputRules}>
                    <Input
                      type='number'
                      onWheel='return false;'
                      label='House Cost'
                      required
                    />
                  </Form.Item>
                  <Form.Item
                    name='approvedLoanAmount'
                    dependencies={['housePurchaseAmount']}
                    rules={[
                      {
                        required: true,
                        message: 'Loan amount is required',
                      },
                      ({ getFieldValue }) => ({
                        validator: (_, value) => {
                          if (
                            !value ||
                            Number(value) <=
                              Number(getFieldValue('housePurchaseAmount'))
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              'Loan amount cannot be greater than house cost'
                            )
                          );
                        },
                      }),
                    ]}
                    style={{
                      marginBottom: '2rem',
                    }}
                  >
                    <Input
                      type='number'
                      label='Disbursed Loan Amount'
                      required
                    />
                  </Form.Item>

                  <Form.Item name='disbursedDate'>
                    <Input
                      type='date'
                      label='Loan Start Date'
                      defaultFocus={true}
                      required
                    />
                  </Form.Item>
                  <Form.Item name='maturityDate'>
                    <Input
                      type='date'
                      label='Loan Maturity Date'
                      defaultFocus={true}
                      required
                    />
                  </Form.Item>
                  <Form.Item name='interestRate'>
                    <Input label='Interest rate' required />
                  </Form.Item>

                  <Form.Item name='loanAccountNumber'>
                    <Input label='Loan Account Number' required />
                  </Form.Item>

                  <Form.Item name='monthlyInstallment'>
                    <Input label='Monthly Installment' required />
                  </Form.Item>

                  <Form.Item name='district'>
                    <Input label='District' required />
                  </Form.Item>
                  <Form.Item name='sector'>
                    <Input label='Sector' required />
                  </Form.Item>
                  <Form.Item name='cell'>
                    <Input label='Cell' required />
                  </Form.Item>
                  <Form.Item name='village'>
                    <Input label='Village' required />
                  </Form.Item>

                  <Form.Item name='perfectionStatus'>
                    <Switch
                      checkedChildren='Yes'
                      unCheckedChildren='No'
                      defaultChecked
                      onChange={onChange}
                    />
                    <br />
                    <label> Status for RDB Perfection</label> <br />
                  </Form.Item>
                  <Form.Item name='perfectionTerminationDate'>
                    <Input
                      type='date'
                      label='Perfection Termination Date'
                      defaultFocus={true}
                      required
                    />
                  </Form.Item>

                  <Form.Item name='fireInsuranceExpiryDate'>
                    <Input
                      type='date'
                      label='Fire Insurance Expiry Date'
                      defaultFocus={true}
                      required
                    />
                  </Form.Item>

                  <Form.Item name='houseUpi' rules={inputRules}>
                    <Input label='Property UPI' required />
                  </Form.Item>
                </div>
              )}

              {bankStatus === 'Disbursed' && (
                <Card style={{ width: '100%' }}>
                  <p>Loan To Value Ratio : {calculateLoanToValueRatio()}%</p>
                  <p>Tenor in months : {calculateTenorInMonths()}</p>
                </Card>
              )}
            </>
          )}
        </Form>
      </Modal>
      <Container>
        <Grid container direction='column'>
          <Grid item>
            <Breadcrumb separator='>'>
              <Breadcrumb.Item
                className='cursor-pointer'
                onClick={() =>
                  history.push(
                    (backendUser || adminUser) && !bankUser
                      ? '/loan/applications'
                      : '/bank'
                  )
                }
              >
                {t('all-applications')}
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t('specific-applications')}</Breadcrumb.Item>
            </Breadcrumb>
          </Grid>
          <Grid item>
            <h5 style={{ fontWeight: '700' }} className='my-5 font-weight-bold'>
              {t('loan-application')}- <b>{application?.data?.referenceId}</b>
            </h5>
          </Grid>
          <div className='d-flex'>
            <div style={{ width: '65%' }}>
              <Grid item className='d-flex flex-column mt-3'>
                <div className='card p-3'>
                  <div className='d-flex justify-content-between'>
                    <h5 style={{ fontWeight: '700' }}>
                      {t('application-status')}
                    </h5>
                    <Tag className='pt-2'>
                      <b>{t('application-date')}</b>: &nbsp;
                      {application?.data?.createdAt
                        ? moment(application?.data?.createdAt).format(
                            'YYYY-MM-DD HH:mmA'
                          )
                        : 'N/A'}
                    </Tag>
                    <label className='text-muted mx-3'>
                      {' '}
                      <small>
                        <RemoveRedEye /> {application?.data?.views?.total}
                      </small>
                    </label>
                  </div>
                  <hr />
                  <div className='d-flex justify-content-between py-3'>
                    <div>
                      <div className='d-flex'>
                        <label className={classes.tableCell}>
                          RHA Status: &nbsp;
                        </label>
                        <label>
                          {' '}
                          <Tag
                            color={getStatusColor(
                              application?.data?.backEndStatus
                            )}
                          >
                            {application?.data?.backEndStatus}
                          </Tag>
                        </label>
                      </div>
                      <div className='d-flex my-2'>
                        <label className={classes.tableCell}>
                          Officer: &nbsp;
                        </label>
                        <label>
                          {application?.data?.backEndStatusChangedByUser
                            ? application?.data?.backEndStatusChangedByUser
                                ?.firstName +
                              ' ' +
                              application?.data?.backEndStatusChangedByUser
                                ?.lastName
                            : 'N/A'}
                        </label>
                      </div>
                      <div className='d-flex'>
                        <label className={classes.tableCell}>
                          Date: &nbsp;
                        </label>
                        <label>
                          {application?.data?.adminStatusChangeDate
                            ? moment(
                                application?.data?.adminStatusChangeDate
                              ).format('YYYY-MM-DD HH:mmA')
                            : 'N/A'}
                        </label>
                      </div>
                    </div>
                    <div className=' mx-5'>
                      <div className='d-flex'>
                        <label className={classes.tableCell}>
                          Bank Status: &nbsp;
                        </label>
                        <label>
                          <Tag
                            color={getStatusColor(
                              application?.data?.bankStatus
                            )}
                          >
                            {application?.data?.bankStatus}
                          </Tag>
                        </label>
                      </div>
                      <div className='d-flex my-2'>
                        <label className={classes.tableCell}>
                          Officer: &nbsp;
                        </label>
                        <label>
                          {application?.data?.bankStatusChangedByUser
                            ? application?.data?.bankStatusChangedByUser
                                ?.firstName +
                              ' ' +
                              application?.data?.bankStatusChangedByUser
                                ?.lastName
                            : 'N/A'}
                        </label>
                      </div>
                      <div className='d-flex'>
                        <label className={classes.tableCell}>
                          Date: &nbsp;
                        </label>
                        <label>
                          {application?.data?.bankStatusChangeDate
                            ? moment(
                                application?.data?.bankStatusChangeDate
                              ).format('YYYY-MM-DD HH:mmA')
                            : 'N/A'}
                        </label>
                      </div>
                    </div>
                    <div className=' mx-5'>
                      <div className='d-flex'>
                        <label className={classes.tableCell}>
                          {t('level')}: &nbsp;
                        </label>
                        <label className='text-capitalize'>
                          <Tag>{application?.data?.level}</Tag>
                        </label>
                      </div>
                    </div>
                  </div>
                  {level === 'bank' &&
                    application?.data?.bankStatus !== 'Disbursed' && (
                      <div className='d-flex justify-content-center my-2'>
                        <Button
                          type='primary'
                          onClick={() => setShowModal(true)}
                        >
                          Update Status
                        </Button>
                      </div>
                    )}
                  {role === 'Back-End Officer' && (
                    <div className='d-flex justify-content-center py-3'>
                      <Button
                        onClick={() => {
                          setAppAction({
                            action: 'Approved',
                            title: 'Approved',
                          });
                          setShowModal(true);
                        }}
                        className='text-success mx-2'
                      >
                        {t('approve')}
                        <CheckCircleFilled />
                      </Button>
                      <Button
                        onClick={() => {
                          setAppAction({
                            action: 'Rejected',
                            title: 'Reject',
                          });
                          setShowModal(true);
                        }}
                        className='text-danger'
                      >
                        {t('reject')}
                        <CloseCircleFilled />
                      </Button>
                    </div>
                  )}
                </div>
                <div className='card p-3 my-2'>
                  <h5 style={{ fontWeight: '700' }}>{t('personal-info')}</h5>
                  <hr />
                  <div className='d-flex justify-content-between flex-wrap'>
                    <div>
                      <div className='d-flex flex-column'>
                        <label className={classes.tableCell}>
                          {t('names')}
                        </label>
                        <label>
                          {application?.data?.applicant?.firstName +
                            ' ' +
                            application?.data?.applicant?.lastName}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-3'>
                        <label className={classes.tableCell}>
                          {t('id-number')}
                        </label>
                        <label>
                          {application?.data?.applicant?.nationalId}
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className='d-flex flex-column'>
                        <label className={classes.tableCell}>{t('dob')}</label>
                        <label>{application?.data?.applicant?.dob}</label>
                      </div>
                      <div className='d-flex flex-column mt-3'>
                        <label className={classes.tableCell}>
                          {t('gender')}
                        </label>
                        <label>{application?.data?.applicant?.gender}</label>
                      </div>
                    </div>
                    <div>
                      <div className='d-flex flex-column'>
                        <label className={classes.tableCell}>
                          {t('marital-status')}
                        </label>
                        <label>
                          {application?.data?.applicant?.maritalStatus}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-3'>
                        <label className={classes.tableCell}>
                          {t('phone-email')}
                        </label>
                        <label>
                          Phone: {application?.data?.applicant?.phone}
                        </label>
                        <label>
                          Email: {application?.data?.applicant?.user?.email}
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 style={{ fontWeight: '700' }}>{t('spouseInfo')}</h5>
                    <hr />
                    {application?.data?.applicant?.spouse ? (
                      <div className='d-flex justify-content-between'>
                        <div>
                          <div className='d-flex flex-column'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('names')}
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse?.firstName +
                                ' ' +
                                application?.data?.applicant?.spouse?.lastName}
                            </label>
                          </div>
                          <div className='d-flex flex-column mt-2'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('id-number')}
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse?.nationalId}
                            </label>
                          </div>
                          <div className='d-flex flex-column mt-2'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('phone')}
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse?.phone}
                            </label>
                          </div>
                        </div>
                        <div>
                          <div className='d-flex flex-column'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('employer')}
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse
                                ?.employerName || 'N/A'}
                            </label>
                          </div>
                          <div className='d-flex flex-column mt-2'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('employer')} (RRA)
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse
                                ?.rraEmployerName || 'N/A'}
                            </label>
                          </div>
                          <div className='d-flex flex-column mt-2'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('profession')}
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse
                                ?.profession || 'N/A'}
                            </label>
                          </div>
                        </div>
                        <div>
                          <div className='d-flex flex-column'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('income')} - Net
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse?.salarynet?.toLocaleString()}
                            </label>
                          </div>
                          <div className='d-flex flex-column mt-2'>
                            <label style={{ fontWeight: 'bold' }}>
                              {t('income')} - RRA Gross
                            </label>
                            <label>
                              {application?.data?.applicant?.spouse?.rraGrossIncome?.toLocaleString() ||
                                'N/A'}
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <label className='text-muted px-2'>
                        {t('no-spouse-info')}
                      </label>
                    )}
                  </div>
                  <hr />
                  <Card>
                    <p>
                      <b>HouseHold Income:</b> RWF{' '}
                      {application?.data &&
                        (
                          application?.data?.applicant.salarynet +
                          (application?.data?.applicant?.spouse?.salarynet || 0)
                        ).toLocaleString()}
                    </p>
                  </Card>
                </div>
                <div className='card p-3 my-2'>
                  <h5 style={{ fontWeight: '700' }}>{t('loan-details')}</h5>
                  <hr />
                  <div className='d-flex justify-content-between py-3'>
                    <div>
                      <div className='d-flex flex-column'>
                        <label className={classes.tableCell}>
                          {t('household-income')}
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.rraCombinedHouseHoldIncome?.toLocaleString()}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-3'>
                        <label className={classes.tableCell}>
                          {t('deductable-income')}
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.deductableIncome?.toLocaleString()}
                        </label>
                      </div>
                    </div>

                    <div className='mx-5'>
                      <div className='d-flex flex-column'>
                        <label className={classes.tableCell}>
                          {t('property-value')}
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.propertyValueLimit?.toLocaleString()}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-3'>
                        <label className={classes.tableCell}>
                          {t('present-value')}
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.presentValue?.toLocaleString()}
                        </label>
                      </div>
                    </div>

                    <div className='mx-5'>
                      <div className='d-flex flex-column'>
                        <label className={classes.tableCell}>
                          {t('loan-provider')}
                        </label>
                        <label> {application?.data?.bankProvider1?.name}</label>
                      </div>
                      <div className='d-flex flex-column mt-3'>
                        <label className={classes.tableCell}>
                          {t('loan-provider2')}
                        </label>
                        <label>
                          {application?.data?.bankProvider2?.name || 'None'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
              <div className='card p-3 my-2'>
                <h5 style={{ fontWeight: '700' }}> {t('employment-info')}</h5>
                <hr />
                <div className='d-flex justify-content-between'>
                  <div>
                    <div className='d-flex flex-column'>
                      <label className={classes.tableCell}>
                        {' '}
                        {t('employer')}
                      </label>
                      <label>
                        {application?.data?.applicant?.employerName || 'N/A'}
                      </label>
                    </div>
                    <div className='d-flex flex-column mt-2'>
                      <label className={classes.tableCell}>
                        {' '}
                        {t('employer')}(RRA)
                      </label>
                      <label>
                        {application?.data?.applicant?.rraEmployerName || 'N/A'}
                      </label>
                    </div>
                    <div className='d-flex flex-column mt-2'>
                      <label style={{ fontWeight: 'bold' }}>
                        TIN({t('employer')})
                      </label>
                      <label>
                        {application?.data?.applicant?.rraEmployerTin || 'N/A'}
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className='d-flex flex-column'>
                      <label className={classes.tableCell}>
                        {t('profession')}
                      </label>
                      <label>{application?.data?.applicant?.profession}</label>
                    </div>
                    <div className='d-flex flex-column mt-2'>
                      <label className={classes.tableCell}>
                        {t('household-income')}
                      </label>
                      <label>
                        RWF{' '}
                        {application?.data?.rraCombinedHouseHoldIncome?.toLocaleString()}
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className='d-flex flex-column'>
                      <label className={classes.tableCell}>{t('Salary')}</label>
                      <label>
                        RWF{' '}
                        {application?.data?.applicant?.salarynet.toLocaleString()}
                      </label>
                    </div>
                    <div className='d-flex flex-column mt-2'>
                      <label style={{ fontWeight: 'bold' }}>
                        {t('gross-income')}(RRA)
                      </label>
                      <label>
                        RWF{' '}
                        {application?.data?.applicant?.rraGrossIncome.toLocaleString()}
                      </label>
                    </div>
                    <div className='d-flex flex-column mt-2'>
                      <label style={{ fontWeight: 'bold' }}>
                        {t('net-income')}(RRA)
                      </label>
                      <label>
                        {application?.data?.applicant?.rraNetIncome
                          ? `RWF
                        ${application?.data?.applicant?.rraNetIncome.toLocaleString()}`
                          : 'N/A'}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className='card p-3 my-2'>
                <h5 style={{ fontWeight: '700' }}> {t('upi-list')}</h5>
                <hr />

                {application?.data?.applicant?.upis?.length > 0 ? (
                  application?.data?.applicant?.upis?.map((u, key) => (
                    <div key={key}>
                      <div
                        className='d-flex justify-content-between my-2'
                        key={key}
                      >
                        <div>
                          <div className='d-flex flex-column'>
                            <label className={classes.tableCell}>
                              {t('upi')}
                            </label>
                            <label>{u.upi}</label>
                          </div>
                          <div className='d-flex flex-column mt-3'>
                            <label className={classes.tableCell}>
                              {t('land-usage')}
                            </label>
                            <label>{u.landUseNameEnglish}</label>
                          </div>
                        </div>
                        <div>
                          <div className='d-flex flex-column'>
                            <label className={classes.tableCell}>
                              {t('primary-owner')}
                            </label>
                            <label>{u.primaryOwner}</label>
                          </div>
                          <div className='d-flex flex-column mt-3'>
                            <label className={classes.tableCell}>
                              {t('under-mortgage')}
                            </label>
                            <label>{u.isUnderMortgage ? 'Yes' : 'No'}</label>
                          </div>
                        </div>
                        <div>
                          <div className='d-flex flex-column'>
                            <label className={classes.tableCell}>
                              {t('is-developed')}
                            </label>
                            <label>
                              {u.isDeveloped === null ? 'No' : 'Yes'}
                            </label>
                          </div>
                          <div className='d-flex flex-column mt-3'>
                            <label className={classes.tableCell}>
                              {t('parcel-location')}
                            </label>
                            <label>{getUPIlocation(u.parcelLocation)}</label>
                          </div>
                        </div>
                      </div>
                      <hr />
                    </div>
                  ))
                ) : (
                  <label className='text-muted px-2'>No UPI Info</label>
                )}
              </div>

              <div className='card p-3 my-2'>
                <h5 style={{ fontWeight: '700' }}>{t('attachments')}</h5>
                <hr />
                <div className='d-flex'>
                  <div>
                    <div className='d-flex flex-column'>
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleZipDownload}
                      >
                        {' '}
                        {t('zip-download')}
                      </label>
                    </div>
                    <div className='d-flex flex-column'>
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleWorkCertificate}
                      >
                        {t('workCertificate')}
                      </label>
                    </div>
                  </div>
                  <div className='mx-5'>
                    <div className='d-flex flex-column'>
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleSpouseWorkCertificate}
                      >
                        {(application?.data?.applicant?.maritalStatus !==
                          'Single' &&
                          t('spouseWorkCertificate')) ||
                          ''}
                      </label>
                    </div>
                    <div className='d-flex flex-column'>
                      <label
                        className={`btn btn-link ${classes.tableCell}`}
                        onClick={handleMaritalCertificate}
                      >
                        {t('maritalCertificate')}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {application?.data?.bankStatus === 'Disbursed' && (
                <div className='card p-3 my-2'>
                  <h5 style={{ fontWeight: '700' }}>
                    {' '}
                    {t('disbursement_details')}
                  </h5>
                  <hr />

                  <div className='d-flex justify-content-between'>
                    <div>
                      <div className='d-flex flex-column'>
                        <label style={{ fontWeight: 'bold' }}>
                          House Purchase Amount
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.housePurchaseAmount?.toLocaleString()}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-2'>
                        <label style={{ fontWeight: 'bold' }}>
                          Approved Loan Amount
                        </label>
                        <label>
                          RWF{' '}
                          {application?.data?.approvedLoanAmount?.toLocaleString()}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-2'>
                        <label style={{ fontWeight: 'bold' }}>
                          Interest Rate
                        </label>
                        <label>{application?.data?.bankInterestRate} %</label>
                      </div>
                    </div>
                    <div>
                      <div className='d-flex flex-column'>
                        <label style={{ fontWeight: 'bold' }}>
                          Loan Account Number
                        </label>
                        <label>
                          {application?.data?.loanAccountNumber || 'N/A'}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-2'>
                        <label style={{ fontWeight: 'bold' }}>
                          Monthly Installment
                        </label>
                        <label>
                          {application?.data?.monthlyInstallment?.toLocaleString() ||
                            'N/A'}{' '}
                          RWF
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-2'>
                        <label style={{ fontWeight: 'bold' }}>Address</label>
                        <label>
                          {application?.data?.district || 'N/A'} -{' '}
                          {application?.data?.sector || 'N/A'} -{' '}
                          {application?.data?.cell || 'N/A'} -{' '}
                          {application?.data?.village || 'N/A'}
                        </label>
                      </div>
                    </div>
                    <div>
                      <div className='d-flex flex-column'>
                        <label style={{ fontWeight: 'bold' }}>
                          Perfection Status
                        </label>
                        <label>
                          {application?.data?.perfectionStatus || 'N/A'}
                        </label>
                      </div>
                      <div className='d-flex flex-column mt-2'>
                        <label style={{ fontWeight: 'bold' }}>
                          Perfection Termination Date
                        </label>
                        <label>
                          {application?.data?.perfectionTerminationDate ||
                            'N/A'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ width: '35%' }}>
              {['Back-End Officer', 'Super Admin', 'Admin'].some((role) =>
                decodeToken().userRole?.RoleName.includes(role)
              ) &&
                application?.data?.backEndStatus == 'Approved' && (
                  <div className='mx-3 card p-3 mt-3'>
                    <Button
                      onClick={() => {
                        setAppAction({
                          action: 'reconfirm',
                          title: t('resendconfmail'),
                        });
                        setShowModal(true);
                      }}
                      className='mx-2 ant-btn ant-btn-primary'
                    >
                      {t('resendconfmail')}
                    </Button>
                  </div>
                )}

              {decodeToken().userRole?.RoleName !== 'Bank Officer' && (
                <div className='mx-3'>
                  <label
                    onClick={() => setShowComments(!showComments)}
                    className='btn btn-link'
                  >
                    {showComments ? t('hide') : t('show')} {t('comments')}
                  </label>
                  {showComments && (
                    <CommentForm
                      applicationComments={application?.data?.documents?.sort(
                        (a, b) => b.id - a.id
                      )}
                      handleDownload={handleDownload}
                      showEditor={showEditor}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </Grid>
      </Container>
      <div>&nbsp;</div>
    </div>
  );
};

export default SpecificApplication;
